import DeckMap from './DeckMap'
import { ASSETS, CLUSTER_ASSETS } from './constant'
import { FlyToInterpolator, type Layer } from '@deck.gl/core'
import { waitMS } from '@/utils/wait'
import type { AssetDataGeoJsonLayer } from '@/plan/filter/asset.type'

// When flying to an extent, deck.gl 9's fitBounds otherwise zooms all the way
// to the viewport max; leave a margin around the extent and cap the zoom
// (mirrors the pre-9 behavior). See deck.gl 9 migration (sc-552667).
const FLY_TO_PADDING_PX = 80
const FLY_TO_MAX_ZOOM = 17

export const tooltipStyle = {
  background: 'var(--colorWhite)',
  color: 'var(--colorHigh)',
  margin: '8px',
  'border-radius': '4px',
  'box-shadow': '0 0 4px 1px rgba(0,0,0, 0.2)',
}

// A deck.gl Layer subclass constructor (IconLayer, TextLayer, the composite layers…).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LayerConstructor = new (...props: any[]) => Layer

interface LayerConfig {
  id: string
  layerType: LayerConstructor
  visible?: boolean
  zIndex?: number
  data?: unknown
  requestOptions?: unknown
  [key: string]: unknown
}

// deck.gl runtime internals we read but which are not on the public typed surface
// (vue3-migration A5). Narrowed locally instead of leaking `any` through the service.
interface DeckLayerInstance {
  id: string
  props: Record<string, unknown>
  state: Record<string, unknown>
}
type BoundingBox = [[number, number], [number, number]]

interface MapViewport {
  metersPerPixel: number
  longitude: number
  latitude: number
  zoom: number
  fitBounds(
    bounds: BoundingBox,
    opts?: { padding?: number; maxZoom?: number },
  ): {
    latitude: number
    longitude: number
    zoom: number
  }
}
interface DeckMapInternals {
  layerManager: {
    layers: DeckLayerInstance[]
    context: { viewport: MapViewport }
  }
}

function internals(deck: DeckMap): DeckMap & DeckMapInternals {
  return deck as DeckMap & DeckMapInternals
}

export default {
  layers: {} as Record<string, LayerConfig>,
  deckInstance: null as DeckMap | null,
  zoom: 0,

  getAppBasemap() {
    if (!this.deckInstance) {
      return 'basemapNotSet'
    }
    return this.deckInstance.getAppBasemap()
  },
  setZoom(zoom: number) {
    this.zoom = zoom
  },
  getZoom() {
    return this.zoom
  },
  init(deckProps: Record<string, unknown>) {
    this.deckInstance = new DeckMap(deckProps)
  },
  destroy() {
    this.deckInstance?.finalize()
    this.deckInstance = null
    this.layers = {}
  },
  hasLayer(id: string) {
    return !!this.layers[id]
  },
  isVisible(id: string) {
    // visible = undefined is treated by deckgl as visible = true
    return this.layers[id] && this.layers[id].visible !== false
  },
  getLayerProp(id: string, prop: string) {
    return this.layers[id] && this.layers[id][prop]
  },
  isAlreadyInitialized() {
    return !!this.deckInstance
  },
  updateDeckInstance(newProps: Record<string, unknown> = {}) {
    if (!this.deckInstance) {
      return
    }
    const layers = Object.values(this.layers)
      .sort((l1, l2) => (l1.zIndex || 0) - (l2.zIndex || 0))
      // NOTE: uncomment next line if you are rendering many (>= 5) MVTLayers at the same time
      // .filter(l => this.isVisible(l.id))
      .map(({ layerType: LayerClass, ...props }) => new LayerClass(props))

    this.deckInstance.setProps({ layers, ...newProps })
  },
  addLayer(layer: LayerConfig, updateDeckInstance = true) {
    if (!layer.id) {
      throw new Error(
        `[layerService.addLayer] layer id must defined. Received "${layer.id}" instead`,
      )
    }
    if (typeof layer.layerType !== 'function') {
      throw new Error(
        `[layerService.addLayer] layerType must be a function. Received "${layer.layerType}" instead`,
      )
    }
    this.layers[layer.id] = layer
    if (updateDeckInstance) this.updateDeckInstance()
  },
  updateLayer(
    id: string,
    data: Partial<LayerConfig>,
    updateDeckInstance = true,
  ) {
    if (!this.layers[id]) {
      console.warn(
        'WARNING: Trying to update layer that it does not exist: ',
        id,
      )
      return
    }

    this.layers[id] = {
      ...this.layers[id],
      ...data,
    }
    if (updateDeckInstance) {
      this.updateDeckInstance()
    }
  },
  removeLayer(id: string, updateDeckInstance = true) {
    if (this.hasLayer(id)) {
      delete this.layers[id]
      if (updateDeckInstance) {
        this.updateDeckInstance()
      }
    } else {
      console.warn(`Layer ${id} not found when trying to remove it`)
    }
  },
  hideLayer(
    id: string,
    opts: Record<string, unknown> = {},
    updateDeckInstance = true,
  ) {
    if (this.hasLayer(id)) {
      this.layers[id].visible = false
      this.updateLayer(id, { ...opts, visible: false }, updateDeckInstance)
    } else {
      console.warn(`Layer ${id} not found when trying to hide it`)
    }
  },
  showLayer(
    id: string,
    opts: Record<string, unknown> = {},
    updateDeckInstance = true,
  ) {
    if (this.hasLayer(id)) {
      this.layers[id].visible = true
      this.updateLayer(id, { visible: true, ...opts }, updateDeckInstance)
    } else {
      console.warn(`Layer ${id} not found when trying to show it`)
    }
  },
  getZoomFactor() {
    if (!this.deckInstance) {
      throw new Error('Deck instance not available when getting zoom factor')
    }
    const viewport = internals(this.deckInstance).layerManager.context.viewport
    return viewport.metersPerPixel
  },
  getViewport() {
    if (!this.deckInstance) {
      throw new Error('Deck instance not available when getting viewport')
    }
    return internals(this.deckInstance).layerManager.context.viewport
  },
  getLayer(layerId: string): DeckLayerInstance | undefined {
    if (!this.deckInstance) {
      return undefined
    }

    const layers = internals(this.deckInstance).layerManager.layers
    const layer = layers.find((l) => l.id === layerId)

    return layer
  },
  getFilteredDataFromAssetLayer(): AssetDataGeoJsonLayer[] {
    const clusterLayer = this.getLayer(CLUSTER_ASSETS)
    const assetLayer = this.getLayer(ASSETS)
    if (!clusterLayer && !assetLayer) {
      console.warn(
        'No assset layer available, neither cluster nor asset. Trying to access to soon? please review from where you are calling this method',
      )
      return []
    }

    if (this.isVisible(CLUSTER_ASSETS)) {
      return (clusterLayer?.props.assetsToShow ||
        clusterLayer?.props.data ||
        []) as AssetDataGeoJsonLayer[]
    }

    if (this.isVisible(ASSETS)) {
      return (assetLayer?.props.assetsToShow ||
        clusterLayer?.props.data ||
        []) as AssetDataGeoJsonLayer[]
    }
    console.warn('No layer visible for assets, neither cluster nor asset')
    return []
  },
  getAssetsPaintendOnMap(): AssetDataGeoJsonLayer[] {
    const clusterLayer = this.getLayer(CLUSTER_ASSETS)
    const assetLayer = this.getLayer(ASSETS)
    if (!clusterLayer && !assetLayer) {
      console.error(
        'No assset layer available, neither cluster nor asset when calling getAssetsPaintendOnMap',
      )
      return []
    }

    if (this.isVisible(CLUSTER_ASSETS)) {
      return (clusterLayer?.props.assetsToShow ||
        clusterLayer?.props.data ||
        []) as AssetDataGeoJsonLayer[]
    }

    if (this.isVisible(ASSETS)) {
      return (assetLayer?.state.data ||
        assetLayer?.props.assetsToShow ||
        clusterLayer?.props.data ||
        []) as AssetDataGeoJsonLayer[]
    }
    console.error(
      'No layer visible for assets, neither cluster nor asset when calling getAssetsPaintendOnMap',
    )
    return []
  },
  getDataFromAssetLayer(): AssetDataGeoJsonLayer[] {
    let layer = this.getLayer(ASSETS)
    let layerName = ASSETS
    if (this.isVisible(CLUSTER_ASSETS)) {
      layer = this.getLayer(CLUSTER_ASSETS)
      layerName = CLUSTER_ASSETS
    }
    if (!layer) {
      // If deck does not has the layer prepared, we got directly for the data
      console.debug(
        `🟡 deck.gl does not have the layer ${layerName} prepared, getting data from backup layers`,
      )
      const backupLayer = this.layers[layerName]
      if (!backupLayer) {
        return []
      } else {
        return (backupLayer.data ?? []) as AssetDataGeoJsonLayer[]
      }
    }
    return (layer.props.data ?? []) as AssetDataGeoJsonLayer[]
  },
  async waitForLayerManagerToBeReady() {
    const maxRetries = 10
    const msToWaitBetweenRetries = 1000
    let retries = 0
    let layerManager = null
    while (!layerManager && retries < maxRetries) {
      try {
        if (!this.deckInstance) {
          throw new Error('Deck instance not available')
        }
        layerManager = internals(this.deckInstance).layerManager
      } catch (e) {
        console.warn(
          `Deck.gl Layer manager not ready when trying to access. Retrying in ${msToWaitBetweenRetries}ms`,
        )
        await waitMS(msToWaitBetweenRetries)
        retries++
      }
    }
    if (retries === maxRetries) {
      throw new Error('Deck.gl Layer manager not ready when trying to access')
    }
  },
  forceIdleRedraw() {
    // Force a single reconcile+draw after a layer update made while the map is
    // idle. deck.gl 9's AnimationLoop does not draw a frame just because
    // setProps flagged a redraw, so an idle update (e.g. excluding assets —
    // layer data changes but the camera does not) is staged but never rendered,
    // leaving stale icons on screen. Only call this when NOT running a camera
    // transition: a forced frame advances the transition clock. See deck.gl 9
    // migration (sc-552667).
    if (!this.deckInstance) {
      return
    }
    this.deckInstance.scheduleForcedRedraw()
  },
  flyTo(boundingBox: BoundingBox) {
    if (!this.deckInstance) {
      console.warn('Deck instance is not initialized, skipping flyTo')
      return
    }
    console.debug(
      '🔰 LayerService: flying to | XMIN:',
      boundingBox[0][0],
      'YMIN:',
      boundingBox[0][1],
      'XMAX:',
      boundingBox[1][0],
      'YMAX:',
      boundingBox[1][1],
    )
    const TRANSITION_DURATION = 750
    const viewport = internals(this.deckInstance).layerManager.context.viewport
    // deck.gl 9's fitBounds is order-sensitive: it reads bounds as
    // [[west, south], [east, north]] and no longer takes the absolute span, so
    // a box whose west > east fits ~340° of longitude (the whole world). FI
    // builds boxes in two orderings (asset extent [[maxLng,minLat],[minLng,
    // maxLat]] vs global/no-data fallback [[minLng,minLat],[maxLng,maxLat]]),
    // so normalize to SW/NE here. (deck.gl 9 migration, sc-552667)
    const west = Math.min(boundingBox[0][0], boundingBox[1][0])
    const east = Math.max(boundingBox[0][0], boundingBox[1][0])
    const south = Math.min(boundingBox[0][1], boundingBox[1][1])
    const north = Math.max(boundingBox[0][1], boundingBox[1][1])
    const deckBounds: BoundingBox = [
      [west, south],
      [east, north],
    ]
    const { latitude, longitude, zoom } = viewport.fitBounds(deckBounds, {
      padding: FLY_TO_PADDING_PX,
      maxZoom: FLY_TO_MAX_ZOOM,
    })
    const viewState = {
      zoom: Math.floor(zoom),
      longitude,
      latitude,
      bearing: 0,
      pitch: 0,
      transitionDuration: TRANSITION_DURATION,
      transitionInterpolator: new FlyToInterpolator(),
    }
    // deck.gl 9's transition clock only advances on rendered frames; without
    // this reset a flyTo issued after the map has been idle starts with the
    // idle gap already elapsed and snaps to the target instead of animating.
    this.deckInstance.resetTransitionClock()
    this.deckInstance.setProps({ viewState })
    // Defer the resize: dispatching it synchronously right after starting the
    // transition cancels the transition in deck.gl 9.
    setTimeout(
      () => window.dispatchEvent(new Event('resize')),
      TRANSITION_DURATION,
    )
  },
  flyToBbox(boundingBox: BoundingBox) {
    this.flyTo(boundingBox)
  },
}
